import { useState } from 'react';
import { motion } from 'motion/react';
import { SortIcon, ScheduleIcon } from './icons';
import { staggerContainer, riseInItem } from './motion';

/**
 * DataTable — port of ~/FrontendFinal/Design/src/components/DataTable.tsx, cut to the THREE columns
 * the backend flow picker draws: Name / Modified / Type. The Design component codes a fourth column
 * ("Last Uploaded"); the user's P1 shows only three, so the fourth column, its header and its cell
 * are removed here and the grid uses `grid-cols-table-3` (see tailwind.config.ts). Everything else —
 * the row states, the shared-rule logic, hover/select behaviour, the icon gutter — is verbatim from
 * the Design. See that file's header for the full SVG provenance and the geometry derivations.
 *
 * STATES, from the sheet:
 *   - THE HIGHLIGHT IS --accent-subtle (0.1), NOT full --accent.
 *   - THE SELECTOR CIRCLE EXISTS ONLY ON HIGHLIGHTED ROWS and does not reserve space when absent.
 *
 * INTERACTION (from the Design, user-requested):
 *   - HOVER shows the teal highlight AND drops the dividing bars on both sides of the hovered row.
 *   - CLICK fills the selector dot; selection is SINGLE, starts EMPTY, and clicking the selected row
 *     toggles it back off.
 *   - A SELECTED row keeps its bars dropped after the pointer leaves.
 *   Hover is tracked in STATE (not CSS `:hover`) because dropping a row's TOP rule means hiding the
 *   PREVIOUS row's bottom border, which CSS has no previous-sibling selector to reach.
 */

export interface TableRow {
  id: string;
  name: string;
  modified: string;
  type: string;
}

export interface DataTableProps {
  rows: TableRow[];
  /** Row id selected on first render (fills its dot). The picker passes nothing, so it starts empty. */
  initialSelectedId?: string;
  /** Notified when the selected row changes (row id, or null when a click deselects). */
  onSelect?: (id: string | null) => void;
}

/** Column headers, in source order, read off the sheet — minus "Last Uploaded". */
const COLUMNS = ['Name', 'Modified', 'Type'] as const;

export function DataTable({ rows, initialSelectedId, onSelect }: DataTableProps) {
  // Selection is single and owned here. Clicking a row selects it; clicking the ALREADY-selected row
  // toggles it back off, clearing the dot and returning the table to empty.
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
  // Hover is tracked in state (see header) — null = nothing hovered.
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const select = (id: string) => {
    const next = id === selectedId ? null : id;
    setSelectedId(next);
    onSelect?.(next);
  };

  // The header's bottom rule is also the FIRST body row's top rule, so when the top row is active its
  // top bar drops too: the header border follows the first row's active state.
  const firstId = rows[0]?.id ?? null;
  const firstActive = firstId != null && (firstId === hoveredId || firstId === selectedId);

  return (
    <div role="table" className="w-full min-w-0">
      {/* Header row. Same height and column grid as a body row; the gutter holds the sort mark. */}
      <div
        role="row"
        className={[
          'grid h-row grid-cols-table-3 items-center border-b bg-surface',
          firstActive ? 'border-surface' : 'border-ink',
        ].join(' ')}
      >
        <div className="relative h-full">
          <SortIcon className="absolute left-icon-inset top-1/2 -translate-y-1/2 text-accent" />
        </div>
        {COLUMNS.map((label) => (
          <div
            role="columnheader"
            key={label}
            className="truncate pr-5 text-sm font-light text-text"
          >
            {label}
          </div>
        ))}
      </div>

      <motion.div variants={staggerContainer(0.04)} initial="hidden" animate="visible">
        {rows.map((row, i) => {
          const selected = row.id === selectedId;
          // A row's bottom rule is shared with the next row's top rule. Hide it when THIS row is
          // active (its bottom) OR the NEXT row is active (that next row's top). "Active" = hovered
          // OR selected.
          const next = rows[i + 1];
          const active = (id: string | null) => row.id === id || (next != null && next.id === id);
          const hideBottomRule = active(selectedId) || active(hoveredId);
          // The FILL belongs only to the row the pointer is over — not the shared `active`.
          const hoveredSelf = row.id === hoveredId;

          return (
            <motion.div
              role="row"
              aria-selected={selected}
              key={row.id}
              variants={riseInItem}
              onClick={() => select(row.id)}
              onMouseEnter={() => setHoveredId(row.id)}
              onMouseLeave={() => setHoveredId((current) => (current === row.id ? null : current))}
              className={[
                'grid h-row grid-cols-table-3 items-center border-b cursor-pointer',
                // The drawn teal highlight (--accent-subtle) is taken when hovered or selected; a
                // selected row keeps it after the pointer leaves.
                selected || hoveredSelf ? 'bg-accent-subtle' : 'bg-surface',
                // border-surface (not transparent) so a dropped rule reads as the highlight's rule.
                hideBottomRule ? 'border-surface' : 'border-ink',
              ].join(' ')}
            >
              {/* Icon gutter, --table-gutter (145) wide. Both glyphs pin at fixed offsets FROM THE
                  ROW'S OWN LEFT EDGE (circle at 32, schedule glyph at 74) — one positioning context
                  with two pinned children (the permitted `absolute` case). */}
              <div className="relative h-full">
                <RowSelector selected={selected} />
                <ScheduleIcon className="absolute left-icon-inset top-1/2 -translate-y-1/2 text-ink" />
              </div>

              {/* Name truncates — the Design's own longest value is already ellipsised. */}
              <div role="cell" className="truncate pr-5 text-sm font-light text-text">
                {row.name}
              </div>
              <div role="cell" className="truncate pr-5 text-sm font-light text-text">
                {row.modified}
              </div>
              <div role="cell" className="truncate pr-5 text-sm font-light text-text">
                {row.type}
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}

/**
 * RowSelector — the circle at cx=32, r=6 filled --ink (the sheet's `selected` specimen). Drawn ONLY
 * on the SELECTED row; an unselected row renders nothing here (no spacer). --table-selector-inset is
 * the circle's CENTRE (32 from the row's left edge), applied as a left offset plus a half-width
 * translate. h-5/w-5 is 12px in THIS project's spacing scale (not Tailwind's default).
 */
function RowSelector({ selected }: { selected: boolean }) {
  if (!selected) return null;

  return (
    <span
      aria-hidden="true"
      className={[
        'absolute left-selector top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2',
        'rounded-full border border-ink bg-ink',
      ].join(' ')}
    />
  );
}
